import { useMemo } from 'react';
import dayjs from 'lib/dayjs';
import { formatNumber } from 'utils/numberFormat';
import { platformLabelMap } from 'services/config/platform';
import type { RefundDrawerStatusTip } from '../types';

interface UseRefundDrawerStatusTipParams {
  platform: General.Platform;
  displayStatus?: string;
  unit?: string;
  refundAmount?: number;
  latestOfferAmount?: number;
  compensationAmount?: number;
  sellerEvidenceDeadlineMs?: string;
  returnShipDueDateMs?: string;
  sellerCompensationDueDateMs?: string;
  dueDateMs?: string;
}

/**
 * 根据 displayStatus 及各类截止时间/金额字段，计算状态描述所需的 date、money、platform。
 * 单一职责：仅负责状态提示数据的计算，供展示层做文案拼接。
 */
export function useRefundDrawerStatusTip(
  params: UseRefundDrawerStatusTipParams,
): RefundDrawerStatusTip {
  const {
    platform,
    displayStatus = '',
    unit = '',
    refundAmount = 0,
    latestOfferAmount = 0,
    compensationAmount = 0,
    sellerEvidenceDeadlineMs = '0',
    returnShipDueDateMs = '0',
    sellerCompensationDueDateMs = '0',
    dueDateMs = '0',
  } = params;

  return useMemo(() => {
    const statusTipObj: RefundDrawerStatusTip = {
      date: '',
      money: '',
      platform: platformLabelMap[platform] ?? '',
    };

    if (
      (displayStatus === 'WAITING_EVIDENCE' || displayStatus === 'DISPUTE_WAIT_EVIDENCE') &&
      sellerEvidenceDeadlineMs !== '0'
    ) {
      statusTipObj.date = dayjs(Number(sellerEvidenceDeadlineMs)).format('YYYY-MM-DD HH:mm:ss');
    } else if (displayStatus === 'RETURN_PROCESSING' && returnShipDueDateMs !== '0') {
      statusTipObj.date = dayjs(Number(returnShipDueDateMs)).format('YYYY-MM-DD HH:mm:ss');
    } else if (displayStatus === 'PENDING_RESPOND') {
      statusTipObj.money = `${unit} ${formatNumber(latestOfferAmount, '0,0.00')}`;
    } else if (displayStatus === 'REFUND_APPROVED') {
      if (sellerCompensationDueDateMs !== '0') {
        statusTipObj.date = dayjs(Number(sellerCompensationDueDateMs)).format(
          'YYYY-MM-DD HH:mm:ss',
        );
      }
      statusTipObj.money = `${unit} ${formatNumber(refundAmount, '0,0.00')}`;
    } else if (displayStatus === 'REFUND_COMPLETED') {
      statusTipObj.money = `${unit} ${formatNumber(refundAmount, '0,0.00')}`;
    } else if (displayStatus === 'WAITING_BUYER_RESPONSE') {
      statusTipObj.money = `${unit} ${formatNumber(latestOfferAmount, '0,0.00')}`;
    } else if (displayStatus === 'REFUND_PENDING' || displayStatus === 'RETURN_REFUND_PENDING') {
      statusTipObj.money = `${unit} ${formatNumber(refundAmount, '0,0.00')}`;
      if (dueDateMs !== '0') {
        statusTipObj.date = dayjs(Number(dueDateMs)).format('YYYY-MM-DD HH:mm:ss');
      }
    } else if (displayStatus === 'REFUND_REQUESTED') {
      statusTipObj.money = `${unit} ${formatNumber(latestOfferAmount, '0,0.00')}`;
      if (dueDateMs !== '0') {
        statusTipObj.date = dayjs(Number(dueDateMs)).format('YYYY-MM-DD HH:mm:ss');
      }
    } else if (displayStatus === 'VALIDATION_IN_PROGRESS') {
      if (dueDateMs !== '0') {
        statusTipObj.date = dayjs(Number(dueDateMs)).format('YYYY-MM-DD HH:mm:ss');
      }
    } else if (displayStatus === 'COMPENSATION_APPROVED') {
      statusTipObj.money = `${unit} ${formatNumber(compensationAmount, '0,0.00')}`;
      if (dueDateMs !== '0') {
        statusTipObj.date = dayjs(Number(dueDateMs)).format('YYYY-MM-DD HH:mm:ss');
      }
    }

    return statusTipObj;
  }, [
    platform,
    displayStatus,
    unit,
    refundAmount,
    latestOfferAmount,
    compensationAmount,
    sellerEvidenceDeadlineMs,
    returnShipDueDateMs,
    sellerCompensationDueDateMs,
    dueDateMs,
  ]);
}
